import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, TrendingUp, Building2, Target, CheckCircle } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const caseStudies = [
  {
    id: "saas-seo-growth",
    company: "B2B SaaS Platform",
    industry: "SaaS",
    service: "GEO & AI SEO",
    challenge: "Struggling to compete with well-funded competitors in search",
    solution: "Implemented AI-powered content engine with GEO optimization",
    results: [
      { value: "312%", label: "Organic Traffic Growth" },
      { value: "156%", label: "Trial Sign-ups Increase" },
      { value: "45%", label: "Lower CAC" },
    ],
    timeline: "6 months",
    testimonial: "The AI-powered approach completely transformed our organic growth trajectory.",
  },
  {
    id: "enterprise-leads",
    company: "Enterprise Software Company",
    industry: "Enterprise",
    service: "AI Agents",
    challenge: "Sales team overwhelmed with unqualified leads, slow response times",
    solution: "Deployed AI lead qualification agents with 24/7 engagement",
    results: [
      { value: "4.2x", label: "Qualified Leads" },
      { value: "< 30s", label: "Response Time" },
      { value: "67%", label: "Rep Time Saved" },
    ],
    timeline: "3 months",
    testimonial: "Our AI agents handle 80% of initial qualification, letting our team focus on closing.",
  },
  {
    id: "b2b-paid-ads",
    company: "Cloud Infrastructure Provider",
    industry: "Cloud/Tech",
    service: "AI Paid Ads",
    challenge: "High CPC and poor ROI on paid campaigns",
    solution: "AI-optimized campaign structure with predictive bidding",
    results: [
      { value: "58%", label: "Lower CPA" },
      { value: "3.2x", label: "ROAS Improvement" },
      { value: "89%", label: "More Conversions" },
    ],
    timeline: "4 months",
    testimonial: "AI optimization reduced our cost per acquisition by more than half.",
  },
  {
    id: "content-velocity",
    company: "Developer Tools Startup",
    industry: "SaaS",
    service: "AI Content",
    challenge: "Couldn't produce enough quality content to compete",
    solution: "AI content engine with human expert editing",
    results: [
      { value: "10x", label: "Content Velocity" },
      { value: "234%", label: "Organic Traffic" },
      { value: "5.1x", label: "Pipeline Growth" },
    ],
    timeline: "5 months",
    testimonial: "We went from 4 posts/month to 40+ without sacrificing quality.",
  },
];

export default function CaseStudies() {
  return (
    <Layout>
      <SEO 
        title="Case Studies | AI Marketing Success Stories"
        description="See how B2B companies achieve 3x+ growth with AI-powered SEO, content, ads, and automation. Real results from real clients."
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        <div className="container-wide relative z-10">
          <AnimatedSection className="text-center max-w-4xl mx-auto">
            <Badge variant="outline" className="mb-4">
              <TrendingUp className="w-3 h-3 mr-1" />
              Proven Results
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
              AI Marketing <span className="text-gradient">Case Studies</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Real results from real clients. See how AI-powered marketing systems 
              drive measurable growth for B2B companies.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-16">
        <div className="container-wide">
          <div className="space-y-12">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative overflow-hidden rounded-3xl border border-border bg-card"
              >
                <div className="p-8 md:p-12">
                  <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                    {/* Left: Details */}
                    <div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <Badge>{study.industry}</Badge>
                        <Badge variant="outline">{study.service}</Badge>
                      </div>
                      
                      <h2 className="text-2xl md:text-3xl font-bold mb-2">
                        {study.company}
                      </h2>
                      
                      <div className="space-y-4 mt-6">
                        <div>
                          <div className="flex items-center gap-2 text-sm font-medium mb-1">
                            <Target className="w-4 h-4 text-red-500" />
                            Challenge
                          </div>
                          <p className="text-muted-foreground">{study.challenge}</p>
                        </div>
                        
                        <div>
                          <div className="flex items-center gap-2 text-sm font-medium mb-1">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Solution
                          </div>
                          <p className="text-muted-foreground">{study.solution}</p>
                        </div>
                      </div>

                      <div className="mt-6 p-4 rounded-xl bg-secondary/50 border border-border">
                        <p className="italic text-muted-foreground">
                          "{study.testimonial}"
                        </p>
                        <p className="text-sm font-medium mt-2">— {study.company}</p>
                      </div>

                      <p className="text-sm text-muted-foreground mt-4">
                        Timeline: {study.timeline}
                      </p>
                    </div>

                    {/* Right: Results */}
                    <div className="flex flex-col justify-center">
                      <h3 className="text-lg font-semibold mb-6">Key Results</h3>
                      <div className="grid gap-6">
                        {study.results.map((result, i) => (
                          <div 
                            key={i}
                            className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-primary/10 to-transparent border border-primary/20"
                          >
                            <div className="text-3xl md:text-4xl font-bold text-primary">
                              {result.value}
                            </div>
                            <div className="text-muted-foreground">
                              {result.label}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-background to-secondary/30">
        <div className="container-wide">
          <AnimatedSection className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Become Our Next Success Story?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Let's discuss how AI-powered marketing can transform your growth trajectory.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="rounded-full"
                data-cal-link="thecloudseo/30-min-meeting"
                data-cal-namespace="30-min-meeting"
                data-cal-config='{"layout":"month_view"}'
              >
                Book a Strategy Call
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="rounded-full"
                asChild
              >
                <Link to="/tools/ai-growth-audit">
                  Get Free Audit
                </Link>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
